import {
  Alert,
  Box,
  Stack,
  Typography,
  Card,
  CardContent,
  Avatar,
  Chip,
  Button,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  LinearProgress,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  useTheme,
  useMediaQuery,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import { useState } from 'react';
import { useHistory } from 'react-router-dom';

import { useCloseGroupNegotiation } from '@/api/endpoints/community/useCloseGroupNegotiation';
import { useDeleteGroupNegotiation } from '@/api/endpoints/community/useDeleteGroupNegotiation';
import { ApiResponse_Get_Group_Negotiations } from '@/api/endpoints/community/useGetGroupNegotiations';
import { useJoinGroupNegotiation } from '@/api/endpoints/community/useJoinGroupNegotiation';
import { useLeaveGroupNegotiation } from '@/api/endpoints/community/useLeaveGroupNegotiation';
import { useReopenGroupNegotiation } from '@/api/endpoints/community/useReopenGroupNegotiation';
import { CalendarIcon } from '@/assets/icons/CalendarIcon';
import { CheckVerified01Icon } from '@/assets/icons/CheckVerified01Icon';
import { DotsVerticalIcon } from '@/assets/icons/DotsVerticalIcon';
import { EyeIcon } from '@/assets/icons/EyeIcon';
import { Lock01Icon } from '@/assets/icons/Lock01Icon';
import { LockUnlocked01Icon } from '@/assets/icons/LockUnlocked01Icon';
import { LogOut01Icon } from '@/assets/icons/LogOut01Icon';
import { MarkerPin01Icon } from '@/assets/icons/MarkerPin01Icon';
import { MessageCircle01Icon } from '@/assets/icons/MessageCircle01Icon';
import { PlusIcon } from '@/assets/icons/PlusIcon';
import { Share01Icon } from '@/assets/icons/Share01Icon';
import { Trash01Icon } from '@/assets/icons/Trash01Icon';
import { UserPlus01Icon } from '@/assets/icons/UserPlus01Icon';
import { Users02Icon } from '@/assets/icons/Users02Icon';
import { PMPRoutes } from '@/config/routes';
import { usePropertyManager } from '@/context/PropertyManagerProvider';

import CreateGroupNegotiationModal from '../../pages/property-manager-portal/CreateGroupNegotiationModal';

import GroupMembersModal from './GroupMembersModal';
import SaveTogetherFirstTimeExperience from './SaveTogetherFirstTimeExperience';
import ShareGroupModal from './ShareGroupModal';

interface GroupNegotiationsSectionProps {
  communityId: number;
  groupNegotiations?: ApiResponse_Get_Group_Negotiations;
  isLoading: boolean;
  isActive: boolean;
  userRole?: string;
}

const GroupNegotiationsSection: React.FC<GroupNegotiationsSectionProps> = ({
  communityId,
  groupNegotiations,
  isLoading,
  isActive,
  userRole,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [createNegotiationModalOpen, setCreateNegotiationModalOpen] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedNegotiationId, setSelectedNegotiationId] = useState<number | null>(null);
  const [currentTab, setCurrentTab] = useState(0);
  const [membersModalOpen, setMembersModalOpen] = useState(false);
  const [selectedGroupForMembers, setSelectedGroupForMembers] = useState<{
    id: number;
    title: string;
  } | null>(null);
  const [closeConfirmDialogOpen, setCloseConfirmDialogOpen] = useState(false);
  const [reopenConfirmDialogOpen, setReopenConfirmDialogOpen] = useState(false);
  const [groupToClose, setGroupToClose] = useState<{ id: number; title: string } | null>(null);
  const [groupToReopen, setGroupToReopen] = useState<{ id: number; title: string } | null>(null);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [selectedGroupForShare, setSelectedGroupForShare] = useState<{
    id: number;
    title: string;
    description?: string;
    memberCount: number;
    metadata?: Record<string, unknown>;
  } | null>(null);
  const [showLocationAlert, setShowLocationAlert] = useState(false);
  const [deleteConfirmDialogOpen, setDeleteConfirmDialogOpen] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState<{ id: number; title: string } | null>(null);

  const history = useHistory();
  const { propertyLocations, isFetching: isLoadingUserProperty } = usePropertyManager();
  const userHasLocation = propertyLocations && propertyLocations.length > 0;
  const joinNegotiationMutation = useJoinGroupNegotiation(undefined, {
    navigateToGroup: (commId, negotiationId) => {
      history.push(`/property-manager/communities/${commId}/groups/${negotiationId}`);
    },
  });
  const leaveNegotiationMutation = useLeaveGroupNegotiation();
  const closeNegotiationMutation = useCloseGroupNegotiation();
  const reopenNegotiationMutation = useReopenGroupNegotiation();
  const deleteNegotiationMutation = useDeleteGroupNegotiation();

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });

  const getDaysRemaining = (endDateString: string) => {
    const endDate = new Date(endDateString);
    const today = new Date();
    const diffTime = endDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return 'Expired';
    if (diffDays === 0) return 'Ends today';
    if (diffDays === 1) return '1 day left';
    return `${diffDays} days left`;
  };

  const isGroupExpired = (endDateString: string) => {
    const endDate = new Date(endDateString);
    const today = new Date();
    return endDate < today;
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, negotiationId: number) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedNegotiationId(negotiationId);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedNegotiationId(null);
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const handleCreateNegotiation = () => {
    if (!userHasLocation) {
      setShowLocationAlert(true);
      return;
    }
    setCreateNegotiationModalOpen(true);
  };

  const handleCreateNegotiationModalClose = () => {
    setCreateNegotiationModalOpen(false);
  };

  const handleJoinNegotiation = (negotiationId: number) => {
    if (!userHasLocation) {
      setShowLocationAlert(true);
      return;
    }
    joinNegotiationMutation.mutate({
      communityId,
      negotiationId,
    });
    handleMenuClose();
  };

  const handleLeaveNegotiation = (negotiationId: number) => {
    leaveNegotiationMutation.mutate({
      communityId,
      negotiationId,
    });
    handleMenuClose();
  };

  const handleCloseNegotiation = (negotiationId: number) => {
    const negotiation = groupNegotiations?.find(n => n.id === negotiationId);
    if (negotiation) {
      setGroupToClose({ id: negotiationId, title: negotiation.title });
      setCloseConfirmDialogOpen(true);
    }
    handleMenuClose();
  };

  const handleReopenNegotiation = (negotiationId: number) => {
    const negotiation = groupNegotiations?.find(n => n.id === negotiationId);
    if (negotiation) {
      setGroupToReopen({ id: negotiationId, title: negotiation.title });
      setReopenConfirmDialogOpen(true);
    }
    handleMenuClose();
  };

  const confirmCloseGroup = () => {
    if (groupToClose) {
      closeNegotiationMutation.mutate({
        communityId,
        negotiationId: groupToClose.id,
      });
      setCloseConfirmDialogOpen(false);
      setGroupToClose(null);
    }
  };

  const confirmReopenGroup = () => {
    if (groupToReopen) {
      reopenNegotiationMutation.mutate({
        communityId,
        negotiationId: groupToReopen.id,
      });
      setReopenConfirmDialogOpen(false);
      setGroupToReopen(null);
    }
  };

  const handleViewMembers = (negotiationId: number, title: string) => {
    setSelectedGroupForMembers({ id: negotiationId, title });
    setMembersModalOpen(true);
  };

  const handleCloseMembersModal = () => {
    setMembersModalOpen(false);
    setSelectedGroupForMembers(null);
  };

  const handleViewGroup = (negotiationId: number) => {
    const groupRoute = PMPRoutes.groupNegotiationView
      .replace(':communityId', communityId.toString())
      .replace(':negotiationId', negotiationId.toString());
    history.push(groupRoute);
    handleMenuClose();
  };

  const handleShareGroup = (negotiationId: number, title: string) => {
    const negotiation = groupNegotiations?.find(n => n.id === negotiationId);
    if (negotiation) {
      setSelectedGroupForShare({
        id: negotiationId,
        title,
        description: negotiation.description,
        memberCount: negotiation.member_count || 0,
        metadata: negotiation.metadata,
      });
      setShareModalOpen(true);
      handleMenuClose();
    }
  };

  const handleCloseShareModal = () => {
    setShareModalOpen(false);
    setSelectedGroupForShare(null);
  };

  const handleDeleteNegotiation = (negotiationId: number) => {
    const negotiation = groupNegotiations?.find(n => n.id === negotiationId);
    if (negotiation) {
      setGroupToDelete({ id: negotiationId, title: negotiation.title });
      setDeleteConfirmDialogOpen(true);
    }
    handleMenuClose();
  };

  const confirmDeleteGroup = () => {
    if (groupToDelete) {
      deleteNegotiationMutation.mutate({
        communityId,
        negotiationId: groupToDelete.id,
      });
      setDeleteConfirmDialogOpen(false);
      setGroupToDelete(null);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Save Together ({groupNegotiations?.length || 0})
          </Typography>
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" color="text.secondary">
              Loading save together groups...
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  const joinedGroups = groupNegotiations?.filter(n => n.is_user_member) || [];
  const availableGroups = groupNegotiations?.filter(n => !n.is_user_member) || [];
  const completedGroups =
    groupNegotiations?.filter(
      n => n.status === 'CLOSED' || (n.end_date && isGroupExpired(n.end_date))
    ) || [];

  const renderGroupCard = (negotiation: ApiResponse_Get_Group_Negotiations[number]) => {
    const maxMembers = negotiation.max_member_count || 0;
    const hasMaxLimit = !!negotiation.max_member_count;
    const progressPercent = hasMaxLimit
      ? Math.round((negotiation.member_count / maxMembers) * 100)
      : 0;
    const remainingMembers = hasMaxLimit ? maxMembers - negotiation.member_count : 0;
    const isExpired = negotiation.end_date && isGroupExpired(negotiation.end_date);
    const isClosed = negotiation.status === 'CLOSED';
    const isGroupActive = negotiation.status === 'ACTIVE' && !isExpired;
    const isFull = hasMaxLimit && negotiation.member_count >= maxMembers;

    // Calculate savings percentage if metadata is available
    const metadata = negotiation.metadata as
      | {
          originalCost?: number;
          savingsAmount?: number;
          estimated_savings?: number;
        }
      | undefined;
    const originalCost = metadata?.originalCost || 0;
    const savingsAmount = metadata?.savingsAmount || metadata?.estimated_savings || 0;
    const finalNegotiatedAmount = originalCost - savingsAmount;
    const savingsPercentage =
      originalCost > 0 && savingsAmount > 0 ? Math.round((savingsAmount / originalCost) * 100) : 0;
    const hasSavingsData = originalCost > 0 && savingsAmount > 0;

    return (
      <Card
        key={negotiation.id}
        variant="outlined"
        sx={{
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          '&:hover': {
            borderColor: 'primary.main',
            boxShadow: 1,
          },
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Stack spacing={3}>
            {/* Header with icon, title, and status */}
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
              <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ flex: 1 }}>
                {hasSavingsData && (
                  <Stack spacing={0.5} alignItems="center">
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: '0.625rem',
                        color: 'success.main',
                        fontWeight: 500,
                        textAlign: 'center',
                        lineHeight: 1,
                      }}
                    >
                      SAVE
                    </Typography>
                    <Tooltip
                      title={`${savingsPercentage}% savings ($${originalCost.toFixed(
                        2
                      )} → $${finalNegotiatedAmount.toFixed(2)})`}
                      placement="top"
                    >
                      <Box
                        sx={{
                          position: 'relative',
                          width: 48,
                          height: 48,
                          borderRadius: 2,
                          backgroundColor: 'success.50',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'success.main',
                          fontWeight: 600,
                          fontSize: '0.875rem',
                          cursor: 'help',
                        }}
                      >
                        {savingsPercentage}%{/* Status circle - only visible on mobile */}
                        {isMobile && (
                          <Box
                            sx={{
                              position: 'absolute',
                              bottom: 10,
                              right: 10,
                              width: 10,
                              height: 10,
                              borderRadius: '50%',
                              backgroundColor: isGroupActive ? '#4caf50' : '#9e9e9e',
                              border: '1px solid white',
                              boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.2)',
                            }}
                          />
                        )}
                      </Box>
                    </Tooltip>
                  </Stack>
                )}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
                    <Typography
                      variant="h6"
                      component="h3"
                      sx={{
                        fontWeight: 600,
                        cursor: 'pointer',
                        '&:hover': {
                          color: 'primary.main',
                          textDecoration: 'underline',
                        },
                      }}
                      onClick={() => handleViewGroup(negotiation.id)}
                    >
                      {negotiation.title}
                    </Typography>
                    {!isMobile && (
                      <Chip
                        label={negotiation.status}
                        size="small"
                        sx={{
                          backgroundColor: isGroupActive ? 'success.50' : 'grey.100',
                          color: isGroupActive ? 'success.main' : 'grey.600',
                          fontWeight: 500,
                          fontSize: '0.75rem',
                        }}
                      />
                    )}
                  </Stack>
                  {!isMobile && negotiation.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                      {negotiation.description}
                    </Typography>
                  )}
                </Box>
              </Stack>
              <IconButton
                size="small"
                onClick={event => handleMenuOpen(event, negotiation.id)}
                sx={{ color: 'text.secondary' }}
              >
                <DotsVerticalIcon />
              </IconButton>
            </Stack>

            {/* Description for mobile - full width */}
            {isMobile && negotiation.description && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  lineHeight: 1.6,
                  mt: -3,
                  mb: 1,
                }}
              >
                {negotiation.description}
              </Typography>
            )}

            {/* Member count and progress */}
            <Box>
              {hasMaxLimit ? (
                <>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{
                      mb: 1,
                      cursor: 'pointer',
                      '&:hover': {
                        color: 'primary.main',
                      },
                    }}
                    onClick={() => handleViewMembers(negotiation.id, negotiation.title)}
                  >
                    <Users02Icon width={16} height={16} />
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {negotiation.member_count} / {maxMembers} members
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
                      {progressPercent}% complete
                    </Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={progressPercent}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: 'grey.100',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: 'primary.main',
                        borderRadius: 4,
                      },
                    }}
                  />
                  {remainingMembers > 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {remainingMembers} more needed to unlock group rate
                    </Typography>
                  )}
                </>
              ) : (
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': {
                      color: 'primary.main',
                    },
                  }}
                  onClick={() => handleViewMembers(negotiation.id, negotiation.title)}
                >
                  <Users02Icon width={16} height={16} />
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {negotiation.member_count} member{negotiation.member_count === 1 ? '' : 's'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    • No limit
                  </Typography>
                </Stack>
              )}
            </Box>

            {/* Date info and invite button */}
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1, sm: 3 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <CalendarIcon width={16} height={16} />
                  <Typography variant="body2" color="text.secondary">
                    Started {formatDate(negotiation.created_at)}
                  </Typography>
                </Stack>
                {negotiation.end_date && (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <CalendarIcon width={16} height={16} />
                    <Typography variant="body2" color="text.secondary">
                      {getDaysRemaining(negotiation.end_date)}
                    </Typography>
                  </Stack>
                )}
              </Stack>
            </Stack>

            {/* Organizer info */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box sx={{ position: 'relative' }}>
                  <Avatar
                    sx={{
                      width: 24,
                      height: 24,
                      fontSize: '0.75rem',
                      backgroundColor: 'grey.300',
                      color: 'grey.700',
                    }}
                  >
                    {(negotiation.initiator.full_name || negotiation.initiator.email)
                      .charAt(0)
                      .toUpperCase()}
                  </Avatar>
                  {negotiation.initiator.is_verified && (
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 0,
                        right: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'white',
                        borderRadius: '50%',
                        width: 8,
                        height: 8,
                      }}
                    >
                      <CheckVerified01Icon sx={{ fontSize: '10px', color: '#c13611' }} />
                    </Box>
                  )}
                </Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                >
                  Organized by {negotiation.initiator.full_name || negotiation.initiator.email}
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                {!negotiation.is_user_member && isGroupActive && !isFull ? (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => handleJoinNegotiation(negotiation.id)}
                    disabled={joinNegotiationMutation.isPending}
                    sx={{
                      minWidth: 'auto',
                      px: 2,
                      py: 0.75,
                      fontSize: '0.875rem',
                      fontWeight: 500,
                    }}
                  >
                    Join Group
                  </Button>
                ) : (
                  <Button
                    variant="outlined"
                    size="small"
                    disabled
                    sx={{
                      minWidth: 'auto',
                      px: 2,
                      py: 0.75,
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      color: 'text.disabled',
                      borderColor: 'divider',
                    }}
                  >
                    {(() => {
                      if (isClosed) return 'Closed';
                      if (isExpired) return 'Expired';
                      if (isFull) return 'Full';
                      return 'Joined';
                    })()}
                  </Button>
                )}
              </Stack>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    );
  };

  const renderEmptyState = (tabIndex: number) => {
    let emptyMessage = '';
    let emptyDescription = '';

    if (tabIndex === 0) {
      emptyMessage = 'No groups joined yet';
      emptyDescription = 'Join a group to start saving together with your neighbors.';
    } else if (tabIndex === 1) {
      emptyMessage = 'No available groups';
      emptyDescription = 'Create a new group to get started with your neighbors.';
    } else {
      emptyMessage = 'No completed groups';
      emptyDescription = 'Completed groups will appear here once they finish.';
    }

    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <MessageCircle01Icon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
        <Typography variant="h6" color="text.secondary" gutterBottom>
          {emptyMessage}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {emptyDescription}
        </Typography>
      </Box>
    );
  };

  // Check if this is a first-time experience (no groups exist at all)
  const totalGroups = groupNegotiations?.length || 0;
  const isFirstTimeExperience = totalGroups === 0 && !isLoading;

  // Show first-time experience if no groups exist
  if (isFirstTimeExperience) {
    return (
      <>
        <Card>
          <CardContent>
            <SaveTogetherFirstTimeExperience
              onCreateGroup={handleCreateNegotiation}
              canCreateGroup={isActive && !!userHasLocation && !isLoadingUserProperty}
            />
          </CardContent>
        </Card>

        {/* Create Group Negotiation Modal */}
        {createNegotiationModalOpen && (
          <CreateGroupNegotiationModal
            communityId={communityId}
            onClose={handleCreateNegotiationModalClose}
          />
        )}

        {/* Location Alert Dialog */}
        <Dialog
          open={showLocationAlert}
          onClose={() => setShowLocationAlert(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle
            sx={{
              fontWeight: 600,
              fontSize: '1.25rem',
              color: 'secondary.900',
              pb: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <MarkerPin01Icon width={24} height={24} />
            Location Required
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1" sx={{ mb: 3 }}>
              To create or join Save Together groups, you need to add your location first. This
              helps us connect you with neighbors in your area for group savings.
            </Typography>
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button variant="outlined" onClick={() => setShowLocationAlert(false)}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  const currentPath = `/property-manager/communities/${communityId}`;
                  window.location.href = `/property-manager/property-locations/create?returnUrl=${encodeURIComponent(
                    currentPath
                  )}`;
                }}
                sx={{
                  bgcolor: 'primary.main',
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'primary.dark',
                  },
                }}
              >
                Add Location
              </Button>
            </Stack>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  return (
    <>
      <Card>
        <CardContent>
          <Box
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}
          >
            <Typography variant="h5" sx={{ fontWeight: 600, color: 'secondary.900', flex: 1 }}>
              Save Together
            </Typography>
            {isActive && !userHasLocation && !isLoadingUserProperty && (
              <Alert
                severity="warning"
                sx={{ mr: 2 }}
                action={
                  <Button size="small" href="/property-manager/property-locations/create">
                    Add Location
                  </Button>
                }
              >
                Add your location to create or join groups
              </Alert>
            )}

            <Tooltip
              title={(() => {
                if (!isActive) {
                  return 'This community is currently inactive. You need to activate it to create groups.';
                }
                if (!userHasLocation) {
                  return 'Add your location to create groups';
                }
                return '';
              })()}
              placement="top"
            >
              <span>
                <Button
                  startIcon={<PlusIcon />}
                  variant="contained"
                  onClick={handleCreateNegotiation}
                  disabled={!isActive || !userHasLocation || isLoadingUserProperty}
                  sx={{
                    bgcolor: 'primary.main',
                    color: 'white',
                    fontWeight: 500,
                    px: 3,
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: 'none',
                    boxShadow: '0px 2px 8px rgba(157, 21, 0, 0.2)',
                    '&:hover': {
                      bgcolor: 'primary.dark',
                      boxShadow: '0px 4px 12px rgba(157, 21, 0, 0.3)',
                    },
                    '&.Mui-disabled': {
                      bgcolor: 'grey.300',
                      color: 'grey.500',
                    },
                  }}
                >
                  {isMobile ? 'Add' : 'Add Save Together'}
                </Button>
              </span>
            </Tooltip>
          </Box>

          {/* Inactive community notice */}
          {!isActive && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="primary.main" sx={{ fontWeight: 500 }}>
                Go to Community Settings → Toggle "Community is active" to enable group creation.
              </Typography>
            </Box>
          )}

          {/* Tabs - Desktop, Dropdown - Mobile */}
          <Box sx={{ mb: 3 }}>
            {/* Desktop Tabs */}
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              <Tabs
                value={currentTab}
                onChange={handleTabChange}
                sx={{
                  '& .MuiTabs-flexContainer': {
                    gap: 0,
                  },
                  '& .MuiTab-root': {
                    minWidth: 'auto',
                    px: 3,
                    py: 1.5,
                    borderRadius: 0,
                    textTransform: 'none',
                    fontWeight: 500,
                    color: 'text.secondary',
                    backgroundColor: 'grey.100',
                    fontSize: '0.875rem',
                    '&:first-of-type': {
                      borderTopLeftRadius: 8,
                      borderBottomLeftRadius: 8,
                    },
                    '&:last-of-type': {
                      borderTopRightRadius: 8,
                      borderBottomRightRadius: 8,
                    },
                    '&.Mui-selected': {
                      color: 'primary.main',
                      backgroundColor: 'primary.100',
                    },
                    '&:hover': {
                      backgroundColor: 'grey.200',
                      '&.Mui-selected': {
                        backgroundColor: 'primary.100',
                      },
                    },
                  },
                  '& .MuiTabs-indicator': {
                    display: 'none',
                  },
                }}
              >
                <Tab
                  label={
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <span>My Groups</span>
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minWidth: 20,
                          height: 20,
                          px: 1,
                          borderRadius: 1,
                          backgroundColor: 'primary.main',
                          color: 'white',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                        }}
                      >
                        {joinedGroups.length}
                      </Box>
                    </Stack>
                  }
                />
                <Tab
                  label={
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <span>Available Groups</span>
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minWidth: 20,
                          height: 20,
                          px: 1,
                          borderRadius: 1,
                          backgroundColor: 'success.main',
                          color: 'white',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                        }}
                      >
                        {availableGroups.length}
                      </Box>
                    </Stack>
                  }
                />
                <Tab
                  label={
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <span>Completed</span>
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minWidth: 20,
                          height: 20,
                          px: 1,
                          borderRadius: 1,
                          backgroundColor: 'grey.600',
                          color: 'white',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                        }}
                      >
                        {completedGroups.length}
                      </Box>
                    </Stack>
                  }
                />
              </Tabs>
            </Box>

            {/* Mobile Dropdown */}
            <Box sx={{ display: { xs: 'block', md: 'none' } }}>
              <FormControl fullWidth>
                <InputLabel id="group-filter-label" sx={{ mt: 1 }}>
                  Filter Groups
                </InputLabel>
                <Select
                  labelId="group-filter-label"
                  value={currentTab}
                  label="Filter Groups"
                  onChange={e => setCurrentTab(Number(e.target.value))}
                  sx={{
                    mt: 1,
                    '& .MuiSelect-select': {
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    },
                  }}
                >
                  <MenuItem value={0}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
                      <span>My Groups</span>
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minWidth: 20,
                          height: 20,
                          px: 1,
                          borderRadius: 1,
                          backgroundColor: 'primary.main',
                          color: 'white',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          ml: 'auto',
                        }}
                      >
                        {joinedGroups.length}
                      </Box>
                    </Stack>
                  </MenuItem>
                  <MenuItem value={1}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
                      <span>Available Groups</span>
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minWidth: 20,
                          height: 20,
                          px: 1,
                          borderRadius: 1,
                          backgroundColor: 'success.main',
                          color: 'white',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          ml: 'auto',
                        }}
                      >
                        {availableGroups.length}
                      </Box>
                    </Stack>
                  </MenuItem>
                  <MenuItem value={2}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
                      <span>Completed</span>
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minWidth: 20,
                          height: 20,
                          px: 1,
                          borderRadius: 1,
                          backgroundColor: 'grey.600',
                          color: 'white',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          ml: 'auto',
                        }}
                      >
                        {completedGroups.length}
                      </Box>
                    </Stack>
                  </MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>

          {/* Tab Content */}
          <Box sx={{ mt: 3 }}>
            {currentTab === 0 && (
              <Stack spacing={2}>
                {joinedGroups.length > 0
                  ? joinedGroups.map(negotiation => renderGroupCard(negotiation))
                  : renderEmptyState(0)}
              </Stack>
            )}

            {currentTab === 1 && (
              <Stack spacing={2}>
                {availableGroups.length > 0
                  ? availableGroups.map(negotiation => renderGroupCard(negotiation))
                  : renderEmptyState(1)}
              </Stack>
            )}

            {currentTab === 2 && (
              <Stack spacing={2}>
                {completedGroups.length > 0
                  ? completedGroups.map((negotiation: ApiResponse_Get_Group_Negotiations[number]) =>
                    renderGroupCard(negotiation)
                  )
                  : renderEmptyState(2)}
              </Stack>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Create Group Negotiation Modal */}
      {createNegotiationModalOpen && (
        <CreateGroupNegotiationModal
          communityId={communityId}
          onClose={handleCreateNegotiationModalClose}
        />
      )}

      {/* Three Dots Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
      >
        {selectedNegotiationId &&
          groupNegotiations &&
          (() => {
            const selectedNegotiation = groupNegotiations.find(n => n.id === selectedNegotiationId);
            if (!selectedNegotiation) return null;

            const isExpired =
              selectedNegotiation.end_date && isGroupExpired(selectedNegotiation.end_date);
            const isClosed = selectedNegotiation.status === 'CLOSED';
            const isGroupActive = selectedNegotiation.status === 'ACTIVE' && !isExpired;
            const isFull =
              selectedNegotiation.max_member_count &&
              selectedNegotiation.member_count >= selectedNegotiation.max_member_count;
            const isUserAdmin = userRole === 'ADMIN';
            const canJoin = !selectedNegotiation.is_user_member && isGroupActive && !isFull;
            const canLeave = selectedNegotiation.is_user_member && isGroupActive;
            const canClose =
              (selectedNegotiation.is_user_initiator || isUserAdmin) && isGroupActive;
            const canReopen = (selectedNegotiation.is_user_initiator || isUserAdmin) && isClosed;
            const canDelete = selectedNegotiation.is_user_initiator && isGroupActive;

            const getDisabledReason = () => {
              if (isClosed) return '(Closed)';
              if (isExpired) return '(Expired)';
              if (isFull) return '(Full)';
              return '(Inactive)';
            };

            const menuItems = [];

            menuItems.push(
              <MenuItem key="view" onClick={() => handleViewGroup(selectedNegotiationId)}>
                <ListItemIcon>
                  <EyeIcon fontSize="small" />
                </ListItemIcon>
                View Group
              </MenuItem>
            );

            // Only show share option for active groups
            if (isGroupActive && userHasLocation) {
              menuItems.push(
                <MenuItem
                  key="share"
                  onClick={() => handleShareGroup(selectedNegotiationId, selectedNegotiation.title)}
                >
                  <ListItemIcon>
                    <Share01Icon fontSize="small" />
                  </ListItemIcon>
                  Share
                </MenuItem>
              );
            }

            if (canJoin && userHasLocation) {
              menuItems.push(
                <MenuItem
                  key="join"
                  onClick={() => handleJoinNegotiation(selectedNegotiationId)}
                  disabled={joinNegotiationMutation.isPending}
                >
                  <ListItemIcon>
                    <UserPlus01Icon fontSize="small" />
                  </ListItemIcon>
                  Join Group
                </MenuItem>
              );
            }

            if (!canJoin && !selectedNegotiation.is_user_member) {
              menuItems.push(
                <MenuItem key="cannot-join" disabled>
                  <ListItemIcon>
                    <UserPlus01Icon fontSize="small" />
                  </ListItemIcon>
                  Cannot Join {getDisabledReason()}
                </MenuItem>
              );
            }

            if (canLeave) {
              menuItems.push(
                <MenuItem
                  key="leave"
                  onClick={() => handleLeaveNegotiation(selectedNegotiationId)}
                  disabled={leaveNegotiationMutation.isPending}
                >
                  <ListItemIcon>
                    <LogOut01Icon fontSize="small" />
                  </ListItemIcon>
                  Leave Group
                </MenuItem>
              );
            }

            if (!canLeave && selectedNegotiation.is_user_member) {
              menuItems.push(
                <MenuItem key="cannot-leave" disabled>
                  <ListItemIcon>
                    <LogOut01Icon fontSize="small" />
                  </ListItemIcon>
                  Cannot Leave {getDisabledReason()}
                </MenuItem>
              );
            }

            if (canClose) {
              menuItems.push(
                <MenuItem
                  key="close"
                  onClick={() => handleCloseNegotiation(selectedNegotiationId)}
                  disabled={closeNegotiationMutation.isPending}
                >
                  <ListItemIcon>
                    <Lock01Icon fontSize="small" />
                  </ListItemIcon>
                  Close Group
                </MenuItem>
              );
            }

            if (canReopen) {
              menuItems.push(
                <MenuItem
                  key="reopen"
                  onClick={() => handleReopenNegotiation(selectedNegotiationId)}
                  disabled={reopenNegotiationMutation.isPending}
                >
                  <ListItemIcon>
                    <LockUnlocked01Icon fontSize="small" />
                  </ListItemIcon>
                  Re-open Group
                </MenuItem>
              );
            }

            if (canDelete) {
              menuItems.push(
                <MenuItem
                  key="delete"
                  onClick={() => handleDeleteNegotiation(selectedNegotiationId)}
                >
                  <ListItemIcon>
                    <Trash01Icon fontSize="small" />
                  </ListItemIcon>
                  Delete Group
                </MenuItem>
              );
            }

            return menuItems;
          })()}
      </Menu>

      {/* Group Members Modal */}
      {membersModalOpen && selectedGroupForMembers && (
        <GroupMembersModal
          open={membersModalOpen}
          onClose={handleCloseMembersModal}
          communityId={communityId}
          negotiationId={selectedGroupForMembers.id}
          groupTitle={selectedGroupForMembers.title}
        />
      )}

      {/* Close Group Confirmation Dialog */}
      <Dialog
        open={closeConfirmDialogOpen}
        onClose={() => setCloseConfirmDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Close Group</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to close "{groupToClose?.title}"? This will prevent new members
            from joining and mark the group as closed.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloseConfirmDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={confirmCloseGroup}
            variant="contained"
            color="primary"
            disabled={closeNegotiationMutation.isPending}
          >
            Close Group
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reopen Group Confirmation Dialog */}
      <Dialog
        open={reopenConfirmDialogOpen}
        onClose={() => setReopenConfirmDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Re-open Group</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to re-open "{groupToReopen?.title}"? This will allow new members
            to join and mark the group as active again.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReopenConfirmDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={confirmReopenGroup}
            variant="contained"
            color="primary"
            disabled={reopenNegotiationMutation.isPending}
          >
            Re-open Group
          </Button>
        </DialogActions>
      </Dialog>

      {/* Share Group Modal */}
      {shareModalOpen && selectedGroupForShare && (
        <ShareGroupModal
          open={shareModalOpen}
          onClose={handleCloseShareModal}
          groupTitle={selectedGroupForShare.title}
          groupId={selectedGroupForShare.id}
          communityId={communityId}
          groupDescription={selectedGroupForShare.description}
          memberCount={selectedGroupForShare.memberCount}
          metadata={
            selectedGroupForShare.metadata as {
              originalCost?: number;
              savingsAmount?: number;
              estimated_savings?: number;
            }
          }
        />
      )}

      {/* Delete Group Confirmation Dialog */}
      <Dialog
        open={deleteConfirmDialogOpen}
        onClose={() => setDeleteConfirmDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Group</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{groupToDelete?.title}"? This action cannot be undone
            and will permanently remove the group.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={confirmDeleteGroup}
            variant="contained"
            color="error"
            disabled={deleteNegotiationMutation.isPending}
          >
            Delete Group
          </Button>
        </DialogActions>
      </Dialog>

      {/* Location Alert Dialog */}
      <Dialog
        open={showLocationAlert}
        onClose={() => setShowLocationAlert(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            fontWeight: 600,
            fontSize: '1.25rem',
            color: 'secondary.900',
            pb: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <MarkerPin01Icon width={24} height={24} />
          Location Required
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 3 }}>
            To create or join Save Together groups, you need to add your location first. This helps
            us connect you with neighbors in your area for group savings.
          </Typography>
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button variant="outlined" onClick={() => setShowLocationAlert(false)}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                const currentPath = `/property-manager/communities/${communityId}`;
                window.location.href = `/property-manager/property-locations/create?returnUrl=${encodeURIComponent(
                  currentPath
                )}`;
              }}
              sx={{
                bgcolor: 'primary.main',
                color: 'white',
                '&:hover': {
                  bgcolor: 'primary.dark',
                },
              }}
            >
              Add Location
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default GroupNegotiationsSection;
